import { getVisibleQuestions } from "./questions";
import type {
  Answers,
  DiscoveryContext,
  GeneratedBrief,
  Question,
  ReadinessItem,
  ReadinessState,
} from "./types";

const verticalLabels: Record<DiscoveryContext["vertical"], string> = {
  film: "Film",
  tv: "TV / series",
  advertising: "Advertising / commercial",
  business: "Business / startup",
  general: "General / other",
};

const serviceLabels: Record<DiscoveryContext["service"], string> = {
  writing: "Writing",
  design: "Design",
  writing_design: "Writing + design",
};

function text(answers: Answers, id: string): string {
  const value = answers[id];
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value?.trim() ?? "";
}

function has(answers: Answers, id: string, minimum = 1): boolean {
  const value = answers[id];
  if (Array.isArray(value)) return value.length >= minimum;
  return typeof value === "string" ? value.trim().length >= minimum : typeof value === "boolean";
}

function missingRequired(questions: Question[], answers: Answers): Question[] {
  return questions.filter((question) => question.required && !has(answers, question.id));
}

function readinessState(present: number, total: number): ReadinessState {
  if (present === 0) return "unknown";
  if (present < total) return "needs_thought";
  return "clear";
}

function readiness(
  answers: Answers,
  context: DiscoveryContext,
  questions: Question[],
): ReadinessItem[] {
  const specs: Array<{
    id: ReadinessItem["id"];
    label: string;
    ids: string[];
    clear: string;
    partial: string;
  }> = [
    {
      id: "decision",
      label: "Decision",
      ids: ["decision.outcome", "audience.primary", "success.after"],
      clear: "The reader, decision, and observable next step are named.",
      partial: "One part of the decision path still needs thought.",
    },
    {
      id: "story",
      label: "Story",
      ids:
        context.vertical === "film"
          ? ["film.logline", "film.synopsis", "film.protagonist"]
          : context.vertical === "tv"
            ? ["tv.logline", "tv.engine", "tv.season_arc"]
            : context.vertical === "advertising"
              ? ["ads.objective", "ads.idea", "ads.product_role"]
              : context.vertical === "business"
                ? ["business.problem", "business.solution", "business.difference"]
                : ["general.kind", "general.change", "general.material"],
      clear: "The route’s central argument has a working spine.",
      partial: "The spine exists, but one essential movement is still open.",
    },
    {
      id: "proof",
      label: "Proof",
      ids:
        context.vertical === "business"
          ? ["business.proof", "business.team"]
          : ["core.materials", context.service === "design" ? "design.rights" : "writing.claims"],
      clear: "Existing material and consequential proof are identified.",
      partial: "Some support exists; important claims or rights still need checking.",
    },
    {
      id: "visual",
      label: "Visual system",
      ids:
        context.service === "writing"
          ? ["audience.setting"]
          : ["design.feeling", "design.formats", "design.brand_system"],
      clear: context.service === "writing" ? "Reading conditions are visible." : "Visual job, formats, and brand authority are visible.",
      partial: "The visual job is partly clear; one operating condition remains open.",
    },
    {
      id: "ask_production",
      label: "Ask / production",
      ids:
        context.vertical === "business"
          ? ["business.ask", "project.stage"]
          : context.vertical === "advertising"
            ? ["ads.deliverables", "ads.production"]
            : ["success.after", "project.stage"],
      clear: "The next action and current making conditions are visible.",
      partial: "The ask or production reality needs one more decision.",
    },
  ];

  return specs.map((spec) => {
    const activeIds = spec.ids.filter((id) => questions.some((question) => question.id === id) || id === "project.stage");
    const present = activeIds.filter((id) => has(answers, id)).length;
    const state = readinessState(present, Math.max(activeIds.length, 1));
    return {
      id: spec.id,
      label: spec.label,
      state,
      reason:
        state === "clear"
          ? spec.clear
          : state === "needs_thought"
            ? spec.partial
            : `No reliable ${spec.label.toLowerCase()} answer yet.`,
    };
  });
}

function outlineFor(context: DiscoveryContext): GeneratedBrief["storyOutline"] {
  const common = [
    { title: "The decision", purpose: "Name the reader, stakes, and exact next action.", sourceQuestionIds: ["decision.outcome", "audience.primary", "success.after"] },
  ];

  const vertical: Record<DiscoveryContext["vertical"], GeneratedBrief["storyOutline"]> = {
    film: [
      { title: "The film in one breath", purpose: "Establish dramatic proposition and form.", sourceQuestionIds: ["film.form", "film.logline"] },
      { title: "Story movement", purpose: "Carry setup, escalation, choice, and change.", sourceQuestionIds: ["film.synopsis"] },
      { title: "People and world", purpose: "Show who carries the story and what pressures them.", sourceQuestionIds: ["film.protagonist", "film.world"] },
      { title: "Meaning and tone", purpose: "Name the idea and emotional contract.", sourceQuestionIds: ["film.themes", "film.tone"] },
      { title: "Why this film can exist", purpose: "Connect creator authority, package, access, and progress.", sourceQuestionIds: ["film.why_now", "film.package", "film.documentary_access"] },
    ],
    tv: [
      { title: "Series promise", purpose: "Establish form, premise, and the repeatable engine.", sourceQuestionIds: ["tv.form", "tv.logline", "tv.engine"] },
      { title: "World and ensemble", purpose: "Make the ongoing dramatic field legible.", sourceQuestionIds: ["tv.overview"] },
      { title: "Pilot and season", purpose: "Show launch, accumulation, and first-season change.", sourceQuestionIds: ["tv.pilot", "tv.season_arc"] },
      { title: "Episode life", purpose: "Demonstrate pattern, variation, and repeatability.", sourceQuestionIds: ["tv.episode_shape", "tv.future"] },
      { title: "Package and path", purpose: "Separate confirmed progress from ambition.", sourceQuestionIds: ["tv.package"] },
    ],
    advertising: [
      { title: "Brief and communication", purpose: "Name the brand, audience change, and proposition.", sourceQuestionIds: ["ads.pitch_type", "ads.brand_product", "ads.objective", "ads.proposition"] },
      { title: "The idea", purpose: "Make action, emotional movement, and product role vivid.", sourceQuestionIds: ["ads.idea", "ads.product_role"] },
      { title: "Treatment language", purpose: "Translate the idea into performance, world, image, sound, and rhythm.", sourceQuestionIds: ["design.feeling", "writing.voice"] },
      { title: "Mandatories and making", purpose: "Reconcile ambition with deliverables, rights, and production conditions.", sourceQuestionIds: ["ads.mandatories", "ads.deliverables", "ads.production"] },
    ],
    business: [
      { title: "Customer and problem", purpose: "Make the costly current situation specific.", sourceQuestionIds: ["business.customer", "business.problem"] },
      { title: "Answer and mechanism", purpose: "Show what changes and why it works.", sourceQuestionIds: ["business.solution", "business.model"] },
      { title: "Proof", purpose: "Connect traction, evidence, and relevant team authority.", sourceQuestionIds: ["business.proof", "business.team"] },
      { title: "Field and difference", purpose: "Place the work among alternatives without market theatre.", sourceQuestionIds: ["business.market", "business.difference"] },
      { title: "The ask", purpose: "End with the exact commitment or next action.", sourceQuestionIds: ["business.ask"] },
    ],
    general: [
      { title: "What this is", purpose: "Orient the reader without forcing a false category.", sourceQuestionIds: ["general.kind", "general.nearest"] },
      { title: "What should change", purpose: "Name the reader movement and stakes.", sourceQuestionIds: ["general.change"] },
      { title: "Essential material", purpose: "Sequence the ideas, events, claims, and proof.", sourceQuestionIds: ["general.material"] },
    ],
  };

  return [...common, ...vertical[context.vertical], {
    title: "What happens next",
    purpose: "Close with one precise action and honest open conditions.",
    sourceQuestionIds: ["success.after", "core.unknowns"],
  }];
}

function assetsFor(context: DiscoveryContext, answers: Answers): string[] {
  const assets = ["Authoritative source material", "Names, roles, credits, and spelling check", "Evidence or sources for consequential claims"];
  if (context.vertical === "film") assets.push("Current script or treatment", "Creator/team biographies", "Confirmed attachment and rights evidence");
  if (context.vertical === "tv") assets.push("Pilot or sample episode", "Season / episode material", "Series package evidence");
  if (context.vertical === "advertising") assets.push("Client/agency brief", "Brand guidelines and mandatories", "Deliverables and production assumptions");
  if (context.vertical === "business") assets.push("Current metrics export", "Customer or market evidence", "Business model and ask inputs");
  if (context.vertical === "general") assets.push("Nearest comparable material", "Project-specific proof");
  if (context.service !== "writing") assets.push("Logo and brand assets", "Rights-cleared image or footage library", "Reference notes: transfer / do not transfer", "Required ratios, formats, and editable-source needs");
  if (context.mode === "private" && !has(answers, "private.source_links")) assets.unshift("Private source links or Drive folder");
  return [...new Set(assets)];
}

export function generateBrief(context: DiscoveryContext, answers: Answers): GeneratedBrief {
  const visible = getVisibleQuestions(context, answers);
  const missing = missingRequired(visible, answers);
  const title = text(answers, "project.title") || "Untitled project";
  const unknowns = text(answers, "core.unknowns");
  const mustAvoid = text(answers, "core.must_avoid");
  const rights = text(answers, "design.rights") || text(answers, "film.documentary_archive");
  const claims = text(answers, "business.proof") || text(answers, "writing.claims") || text(answers, "film.package") || text(answers, "tv.package");

  const openQuestions = missing.map((question) => question.label);
  if (unknowns) openQuestions.unshift(unknowns);

  const risks = [
    mustAvoid ? `Protected boundary: ${mustAvoid}` : "No explicit ‘must avoid’ boundary has been recorded.",
    rights ? `Rights / permission state: ${rights}` : "Rights and permission state may still need review.",
  ];
  if (!has(answers, "audience.objections")) risks.push("Reader objections have not been named yet.");
  if (context.mode === "private" && !has(answers, "private.stakeholders")) risks.push("Final decision owner is not clear.");

  const doNotInvent = [
    "Do not turn unknown, hoped-for, or in-conversation material into confirmed fact.",
    "Do not create a source, quote, attachment, permission, budget, date, or audience claim that was not supplied.",
  ];
  if (unknowns) doNotInvent.push(`Keep these open: ${unknowns}`);
  if (rights) doNotInvent.push("Do not treat available media as cleared media without the stated permission.");

  const ready = readiness(answers, context, visible);
  const weakest = ready.filter((item) => item.state !== "clear").slice(0, 3);
  const nextActions = weakest.map((item) => `Resolve ${item.label.toLowerCase()}: ${item.reason}`);
  while (nextActions.length < 3) {
    const defaults = [
      "Confirm the authority order for source material and approvals.",
      "Collect inspectable proof for each consequential claim.",
      "Turn this brief into a first deck outline, then test it against the primary reader.",
    ];
    const next = defaults.find((item) => !nextActions.includes(item));
    if (!next) break;
    nextActions.push(next);
  }

  const projectBrief = [
    { label: "Project", value: title },
    { label: "Route", value: `${verticalLabels[context.vertical]} · ${serviceLabels[context.service]}` },
    { label: "Stage", value: text(answers, "project.stage") || "Unknown" },
    { label: "Decision", value: text(answers, "decision.outcome") || "Not answered yet" },
    { label: "Primary reader", value: text(answers, "audience.primary") || "Not answered yet" },
    { label: "Observable success", value: text(answers, "success.after") || "Not answered yet" },
  ];

  return {
    title,
    routeLabel: `${verticalLabels[context.vertical]} · ${serviceLabels[context.service]} · ${context.mode === "public" ? "Public self-study" : "Private client"}`,
    generatedAt: new Date().toISOString(),
    projectBrief,
    storyOutline: outlineFor(context),
    assetChecklist: assetsFor(context, answers),
    evidenceIndex: [
      {
        claim: claims || "No consequential evidence has been supplied yet.",
        source: claims ? "See supplied response; source location still needs line-level checking." : "Missing",
        state: claims ? "needs_thought" : "unknown",
      },
    ],
    openQuestions: openQuestions.length ? openQuestions : ["No required question is currently unanswered. Human review may still find a gap."],
    risks,
    doNotInvent,
    nextActions: nextActions.slice(0, 3),
    readiness: ready,
  };
}

export function briefToMarkdown(brief: GeneratedBrief): string {
  const lines = [
    `# ${brief.title}`,
    "",
    brief.routeLabel,
    `Generated ${brief.generatedAt}`,
    "",
    "## Project and decision",
    ...brief.projectBrief.map((item) => `- **${item.label}:** ${item.value}`),
    "",
    "## Recommended deck story",
    ...brief.storyOutline.map((item, index) => `${index + 1}. **${item.title}** — ${item.purpose}`),
    "",
    "## Asset checklist",
    ...brief.assetChecklist.map((item) => `- [ ] ${item}`),
    "",
    "## Evidence index",
    ...brief.evidenceIndex.map((item) => `- **${item.state}:** ${item.claim} — ${item.source}`),
    "",
    "## Open questions",
    ...brief.openQuestions.map((item) => `- ${item}`),
    "",
    "## Risks",
    ...brief.risks.map((item) => `- ${item}`),
    "",
    "## Do not invent or imply",
    ...brief.doNotInvent.map((item) => `- ${item}`),
    "",
    "## Three next actions",
    ...brief.nextActions.map((item, index) => `${index + 1}. ${item}`),
    "",
    "## Deck Readiness Map",
    ...brief.readiness.map((item) => `- **${item.label} — ${item.state.replace("_", " ")}:** ${item.reason}`),
    "",
  ];
  return lines.join("\n");
}
